import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { LinePath, Line } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import type { TaperDayWire } from '@shared/types';

// A tiny "graph within a graph" strip that lives under the main
// TaperChart. It always shows the full plan in miniature so users
// don't lose long-arc context when the main chart is zoomed to a
// Week or Month window. The shaded rect indicates which slice of the
// plan the main chart is currently showing; the copper vertical line
// marks today.
//
// Non-interactive in v1 — keyboard control of the view is via the
// SegmentedControl above. aria-hidden because everything it conveys
// is also in the main chart's axes and the Day-X-of-Y subtitle.

interface TaperMinimapProps {
  days: TaperDayWire[];
  todayIndex?: number;
  windowStartIndex: number;
  windowEndIndex: number;
}

interface Point { i: number; mg: number; }

export function TaperMinimap(props: TaperMinimapProps) {
  return (
    <div aria-hidden="true" style={{ width: '100%', height: 40 }}>
      <ParentSize>
        {({ width, height }) => <TaperMinimapInner {...props} width={width} height={height} />}
      </ParentSize>
    </div>
  );
}

function TaperMinimapInner({
  days,
  todayIndex,
  windowStartIndex,
  windowEndIndex,
  width,
  height,
}: TaperMinimapProps & { width: number; height: number }) {
  // Match the main chart's horizontal margins so the highlight aligns
  // visually with the chart above it. Vertical padding is tight; the
  // strip is a glance, not a chart.
  const margin = { top: 4, right: 28, bottom: 4, left: 36 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const points: Point[] = days.map((d) => ({ i: d.dayIndex, mg: d.targetMg }));
  const maxMg = Math.max(1, ...days.map((d) => d.targetMg));
  const x = scaleLinear<number>({
    domain: [0, Math.max(1, days.length - 1)],
    range: [0, innerW],
  });
  const y = scaleLinear<number>({ domain: [0, maxMg], range: [innerH, 0] });

  if (width === 0 || innerW === 0) return null;

  const lastIndex = Math.max(0, days.length - 1);
  const startClamped = Math.max(0, Math.min(lastIndex, windowStartIndex));
  const endClamped = Math.max(startClamped, Math.min(lastIndex, windowEndIndex));
  const xStart = x(startClamped);
  const xEnd = x(endClamped);
  // Always ≥ 2px so a 1-day window is still visible as a highlight.
  const rectW = Math.max(2, xEnd - xStart);

  return (
    <svg width={width} height={height}>
      <Group left={margin.left} top={margin.top}>
        <rect
          x={xStart}
          y={0}
          width={rectW}
          height={innerH}
          fill="var(--chart-actual)"
          fillOpacity={0.15}
          stroke="var(--chart-actual)"
          strokeWidth={1}
          strokeOpacity={0.6}
          rx={2}
        />
        <LinePath<Point>
          data={points}
          x={(d) => x(d.i)}
          y={(d) => y(d.mg)}
          curve={curveMonotoneX}
          stroke="var(--chart-target)"
          strokeWidth={1.5}
          strokeOpacity={0.75}
        />
        {typeof todayIndex === 'number' && todayIndex >= 0 && todayIndex <= lastIndex ? (
          <Line
            from={{ x: x(todayIndex), y: 0 }}
            to={{ x: x(todayIndex), y: innerH }}
            stroke="var(--chart-today-marker)"
            strokeWidth={1.5}
          />
        ) : null}
      </Group>
    </svg>
  );
}
